import { cn } from "@lib/cn"
import { formatRelativeShort } from "@lib/intl"
import { Archive, Check } from "lucide-react"
import { useTranslation } from "react-i18next"

import { TONE_CLASSES, visualForKind } from "../icons"

import type { Notification } from "@/types"

interface Props {
  notification: Notification
  onClick: () => void
  onMarkRead?: (id: string) => void
  onArchive?: (id: string) => void
  /** Shrinks padding and hides the row actions — the bell dropdown. */
  compact?: boolean
}

/**
 * The one notification row, used by the bell dropdown and by
 * `/notificaciones`.
 *
 * PRD-59 — the row used to be a `<button>` with two `role="button"` spans
 * inside it. Interactive content inside a button is invalid, and those spans
 * were the div-as-button antipattern `accessibility.md` §1 names: focusable,
 * keyboard-handled by hand, and depending on `stopPropagation` not to fire the
 * row underneath. The row is now a plain container with a stretched button for
 * "open this", and the two actions are real buttons above it.
 */
export function NotificationCard({
  notification,
  onClick,
  onMarkRead,
  onArchive,
  compact = false,
}: Props) {
  const { t } = useTranslation("notifications")
  const { Icon, tone } = visualForKind(notification.kind)
  const toneCls = TONE_CLASSES[tone]
  const isUnread = !notification.read
  const showActions = (onMarkRead || onArchive) && !compact

  return (
    <div
      className={cn(
        "rounded-card bg-surface relative flex w-full items-start gap-3 border transition-colors duration-150",
        compact ? "p-2.5" : "p-3",
        isUnread ? "border-border hover:border-accent" : "border-hairline bg-inset",
      )}
    >
      {/* The whole row opens the notification; the actions below sit above this
          layer. `::after` rather than a sized overlay so the button can carry
          the row's accessible name. The focus ring is drawn on that ::after —
          the button itself is 0x0, so a ring on it was a dot in the corner. The `!`
          beats the unlayered global :focus-visible outline in index.css. */}
      <button
        type="button"
        onClick={onClick}
        aria-label={t(isUnread ? "card.open_unread" : "card.open", { title: notification.title })}
        className="after:rounded-card focus-visible:after:ring-accent after:absolute after:inset-0 after:content-[''] focus-visible:outline-none! focus-visible:after:ring-2"
      />

      <span
        className={cn(
          "rounded-control flex h-9 w-9 flex-shrink-0 items-center justify-center",
          toneCls.chip,
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {/* Two lines, not one: on a phone a one-line title lost the exam's
              name, which is the part that says which notification this is. */}
          <span
            className={cn(
              "line-clamp-2 min-w-0 text-sm",
              isUnread ? "text-ink font-semibold" : "text-ink-muted font-medium",
            )}
          >
            {notification.title}
          </span>
          <span className="text-meta ml-auto flex flex-shrink-0 items-center gap-1.5">
            {/* In the row's flow, not pinned to the corner: absolutely placed,
                it sat on top of the timestamp. */}
            {isUnread ? (
              <span className={cn("h-2 w-2 rounded-full", toneCls.dot)} aria-hidden />
            ) : null}
            {formatRelativeShort(notification.created_at)}
          </span>
        </div>
        <p
          className={cn(
            "mt-0.5 line-clamp-2 text-xs",
            isUnread ? "text-ink-muted" : "text-ink-subtle",
          )}
        >
          {notification.message}
        </p>

        {showActions ? (
          <div className="relative z-10 -mx-1 mt-1 flex items-center gap-1 text-xs">
            {onMarkRead && isUnread ? (
              <button
                type="button"
                onClick={() => onMarkRead(notification.id)}
                className="rounded-control text-accent-ink hover:bg-accent-soft inline-flex min-h-11 items-center gap-1 px-2 md:min-h-8"
              >
                <Check className="h-3.5 w-3.5" aria-hidden />
                {t("actions.mark_read")}
              </button>
            ) : null}
            {onArchive ? (
              <button
                type="button"
                onClick={() => onArchive(notification.id)}
                className="rounded-control text-ink-muted hover:bg-inset hover:text-ink inline-flex min-h-11 items-center gap-1 px-2 md:min-h-8"
              >
                <Archive className="h-3.5 w-3.5" aria-hidden />
                {t("actions.archive")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
